import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaDownload, FaExternalLinkAlt, FaFilePdf, FaClock } from 'react-icons/fa';
import { fetchSharedResource } from '../../Services/shareLinks';
import CertificateDocument from '../AdminRoutes/Certificates/CertificateDocument';
import { certificateToForm } from '../AdminRoutes/Certificates/certificateTemplates';
import { certificateNodeToPdf, certificateFileName } from '../AdminRoutes/Certificates/certificatePdf';

// html2canvas paints whatever is on screen at that instant, so a letterhead
// captured before its logo loads comes out blank.
const waitForImages = (node) =>
  Promise.all(
    Array.from(node?.querySelectorAll('img') || []).map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
            setTimeout(resolve, 3000);
          })
    )
  );

const fmtDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const countdown = (expiresAt, now) => {
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes || 1} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
};

const Shell = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      {children}
    </div>
  </div>
);

/**
 * Patient facing certificate page. Reached through the short lived link staff
 * share, with no login: the token in the URL is the only credential. The PDF is
 * built here in the browser from the same CertificateDocument the clinic sees,
 * so the shared copy and the printed copy can never drift apart.
 */
export default function SharedCertificate() {
  const { token } = useParams();

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [building, setBuilding] = useState(true);
  const [now, setNow] = useState(Date.now());

  // Scale the fallback preview down to whatever width the phone gives us.
  const [previewScale, setPreviewScale] = useState(1);

  // Mobile browsers (iOS Safari especially) will not render a PDF inside an
  // iframe, so small screens get the picture and the buttons instead.
  const [canEmbed, setCanEmbed] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );

  const documentRef = useRef(null);
  const pdfRef = useRef(null);
  const previewBoxRef = useRef(null);

  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)');
    const onChange = (event) => setCanEmbed(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Certificate and loading flip together, so the hidden document is already
    // mounted by the time the PDF effect below looks for it.
    fetchSharedResource(token)
      .then((data) => {
        if (cancelled) return;
        // The resolver serves every kind of share token, so make sure this one
        // really is a certificate before rendering it as one.
        setCertificate(data?.kind === 'certificate' ? data : null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.status === 410
            ? 'This link has expired. Please ask the clinic to send you a new one.'
            : err?.response?.status === 404
            ? 'This link is not valid any more.'
            : 'We could not open this certificate. Please try again shortly.'
        );
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const form = useMemo(() => (certificate ? certificateToForm(certificate) : null), [certificate]);

  // Build the PDF once the hidden document has rendered with its images.
  useEffect(() => {
    if (!certificate) return undefined;

    let cancelled = false;
    let objectUrl = null;

    (async () => {
      try {
        await waitForImages(documentRef.current);
        if (cancelled || !documentRef.current) return;

        // One frame for the letterhead to settle before it is captured.
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (cancelled || !documentRef.current) return;

        const pdf = await certificateNodeToPdf(documentRef.current);
        if (cancelled) return;

        pdfRef.current = pdf;
        objectUrl = URL.createObjectURL(pdf.output('blob'));
        setPdfUrl(objectUrl);
      } catch (err) {
        // The certificate itself still renders below, so this costs the PDF
        // buttons rather than the whole page.
        console.log('Could not build the certificate PDF:', err);
        if (!cancelled) setPdfFailed(true);
      } finally {
        if (!cancelled) setBuilding(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [certificate]);

  // Keep the fallback preview fitted to its box as the phone is rotated.
  useEffect(() => {
    const box = previewBoxRef.current;
    if (!box) return undefined;

    const fit = () => setPreviewScale(Math.min(box.clientWidth / 794, 1));
    fit();

    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [building, canEmbed, pdfUrl]);

  const download = useCallback(() => {
    if (!pdfRef.current) return;
    pdfRef.current.save(certificateFileName(certificate));
  }, [certificate]);

  if (loading) {
    return (
      <Shell>
        <p className="text-sm text-slate-500">Opening your certificate…</p>
      </Shell>
    );
  }

  if (error || !certificate) {
    return (
      <Shell>
        <FaClock className="mx-auto mb-3 text-2xl text-slate-400" />
        <h1 className="text-lg font-semibold text-slate-800">Link unavailable</h1>
        <p className="mt-2 text-sm text-slate-500">{error || 'This link is not valid any more.'}</p>
      </Shell>
    );
  }

  const remaining = countdown(certificate.expiresAt, now);

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-slate-800">{certificate.title}</h1>
            <p className="truncate text-xs text-slate-500">
              {certificate.patientName}
              {certificate.certificateNo ? ` · ${certificate.certificateNo}` : ''}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={download}
              disabled={building || pdfFailed}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              <FaDownload /> {building ? 'Preparing…' : 'Download PDF'}
            </button>
            <a
              href={pdfUrl || undefined}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 ${
                pdfUrl ? '' : 'pointer-events-none opacity-60'
              }`}
            >
              <FaExternalLinkAlt /> Open PDF
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <FaClock className="mt-0.5 shrink-0 text-amber-600" />
          <div>
            <p className="font-semibold">
              {remaining ? `This link expires in ${remaining}` : 'This link has expired'}
            </p>
            <p className="mt-0.5 text-amber-800">
              Valid until {fmtDateTime(certificate.expiresAt)}. Download the PDF to keep a permanent copy —
              after that the clinic will need to send you a new link.
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {building && (
            <p className="px-4 py-16 text-center text-sm text-slate-500">
              <FaFilePdf className="mx-auto mb-3 text-2xl text-slate-300" />
              Preparing your certificate…
            </p>
          )}

          {!building && pdfFailed && (
            <p className="border-b border-slate-100 bg-slate-50 px-4 py-3 text-center text-xs text-slate-600">
              The PDF could not be prepared on this browser. You can still read the certificate below, or
              print this page to save a copy.
            </p>
          )}

          {!building && pdfUrl && canEmbed && (
            <iframe
              title={certificate.title}
              src={pdfUrl}
              className="h-[80vh] w-full border-0"
            />
          )}

          {/* Phones cannot show the embedded PDF, so they get the page itself,
              scaled to fit, with the buttons above doing the real work. */}
          {!building && (!canEmbed || !pdfUrl) && (
            <div ref={previewBoxRef} className="p-3">
              <div style={{ height: 1123 * previewScale, overflow: 'hidden' }}>
                <div
                  style={{
                    width: 794,
                    transform: `scale(${previewScale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <CertificateDocument form={form} domId="certificate-shared-preview" />
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-slate-500">
          Issued by {certificate.clinicName} on {fmtDateTime(certificate.issuedOn)}
        </p>
      </main>

      {/* Off-screen full size render that the PDF is captured from. */}
      <div style={{ position: 'fixed', top: 0, left: '-10000px', zIndex: -1 }}>
        <CertificateDocument form={form} ref={documentRef} domId="certificate-shared-source" />
      </div>
    </div>
  );
}
